import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Col, Container, Row } from 'react-bootstrap'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import Trans from 'next-translate/Trans'

import styles from './style.module.scss'
import Layout from '@shared-components/layout'
import { matchDetailNav } from '@shared/constants/allNavBars'
import { navLoader, pageHeaderLoader, tableLoader } from '@shared/libs/allLoader'
import { checkIsGlanceView, isWithinMinutes } from '@shared/utils'
import Error from '@shared/components/error'
import { setMatchPlayer } from '@shared/libs/match-detail'
import { changeSeoData } from '@shared/libs/meta-details/match'
import usePolling from '@shared/hooks/usePolling'
import { MATCH_DETAIL } from '@graphql/match/match.query'
import { allRoutes } from '@shared/constants/allRoutes'

const Skeleton = dynamic(() => import('@shared/components/skeleton'), { ssr: false })
const MatchXI = dynamic(() => import('@shared/components/match/matchXI'))
const MatchHeader = dynamic(() => import('@shared-components/match/matchHeader'), { loading: () => pageHeaderLoader() })
const RecentOver = dynamic(() => import('@shared-components/match/recentOver'), { loading: () => <div style={{ height: '48px' }} /> })
const CommonNav = dynamic(() => import('@shared-components/commonNav'), { loading: () => navLoader() })
const CurrentSeries = dynamic(() => import('@shared/components/widgets/currentSeries'))
const TrendingNews = dynamic(() => import('@shared/components/trendingNews'))
const CommonContent = dynamic(() => import('@shared/components/commonContent'))
const MatchDetailOverview = dynamic(() => import('@shared-components/match/matchDetailOverview'), {
  loading: () => (
    <div className="bg-white rounded p-3">
      {Array.from(Array(5)).map((e, i) => {
        return (
          <React.Fragment key={i}>
            <div className="d-flex">
              <Skeleton width={'10%'} className={'me-3'} />
              <Skeleton width={'25%'} />
            </div>
            <hr />
          </React.Fragment>
        )
      })}
    </div>
  )
})
const MatchDetailScorecard = dynamic(() => import('@shared-components/match/matchDetailScorecard'), { loading: () => tableLoader() })
const MatchDetailFantasyTips = dynamic(() => import('@shared/components/match/matchDetailFantasyTips'), { loading: () => tableLoader() })
const MatchDetailFantasyPlatForm = dynamic(() => import('@shared/components/match/matchDetailFantasyPlatForm'), {
  loading: () => tableLoader()
})
// const MatchDetailCommentary = dynamic(() => import('@shared/components/match/matchDetailCommentary'), { loading: () => tableLoader() })
const CommentaryV2 = dynamic(() => import('@shared/components/match/matchDetailCommentary/commentryv2'), { loading: () => tableLoader() })
const MatchDetailUpcoming = dynamic(() => import('@shared/components/match/matchDetailUpcoming'), { loading: () => tableLoader() })
const MatchDetailNews = dynamic(() => import('@shared/components/match/matchDetailNews'), { loading: () => tableLoader() })
const MatchDetailOvers = dynamic(() => import('@shared/components/match/matchDetailOvers'), { loading: () => tableLoader() })
const LiveArticle = dynamic(() => import('@shared/components/articleDetail/liveArticle'))
const Timeline = dynamic(() => import('@shared/components/articleDetail/timeline'), { ssr: false })
const TimelineDropdown = dynamic(() => import('@shared/components/articleDetail/timelineDropdown'), { ssr: false })
const ReelMain = dynamic(() => import('@shared/components/reelSlider/reelMain'), { ssr: false })

// const MatchDetailStandings = dynamic(() => import('@shared/components/match/matchDetailStandings'))
const NoData = dynamic(() => import('@noData'), { ssr: false })
const Ads = dynamic(() => import('@shared/components/ads'))
const GlanceAd = dynamic(() => import('@shared-components/ads/glanceAd'), { ssr: false })
const MatchPoll = dynamic(() => import('@shared/components/multiPoll/matchPoll'))
// const CricketPedia = dynamic(() => import('@shared/components/widgets/cricketPedia'))

const MatchDetail = ({
  matchDetail,
  type,
  scorecard,
  fantasyData,
  fantasyPlatFormData,
  overViewData,
  commentaryData,
  matchOverView,
  squadData,
  upcomingData,
  newsData,
  recentOverData,
  liveScoreData,
  seoData,
  roundData,
  resultData,
  playingXI,
  liveArticleData,
  sideBarData,
  catSubPages
}) => {
  setMatchPlayer(playingXI || {})
  const router = useRouter()
  const [timelineData, setTimelineData] = useState()
  const [matchData, setMatchData] = useState(matchDetail)
  const commentaryOpen = useRef(false)
  const isGlanceView = checkIsGlanceView(router?.query)
  const status = matchData?.sStatusStr
  const categoryPages = getPagesURL(catSubPages)
  const seo = changeSeoData(seoData, matchDetail, type)

  usePolling({
    query: MATCH_DETAIL,
    isEnable: isWithinMinutes(matchData?.dStartDate),
    interval: 60000,
    variables: { input: { _id: matchDetail?._id } },
    onUpdate: (d) => setMatchData(d?.getMatchById)
  })

  const handleCommentary = () => {
    commentaryOpen.current = true
  }

  function getPagesURL(data) {
    return data?.reduce((acc, e) => {
      acc[e?.eSubType ?? 'h'] = e
      return acc
    }, {})
  }

  useEffect(() => {
    !timelineData && setTimelineData(liveArticleData?.oLiveArticleList)
  }, [liveArticleData?.oLiveArticleList])

  const tab = () => {
    // if (type === 'full-scorecard') return (!matchData?.bIsCommentary && (scorecard?.fetchLiveInningsData?.length === 0 || scorecard?.fetchLiveInningsData[0]?.aBatters?.length === 0)) ? <NoData /> : <MatchDetailScorecard style={styles} data={scorecard} squad={squadData} matchDetail={matchData} matchOverView={matchOverView} />
    if (type === 'full-scorecard') {
      if (
        matchData?.sStatusStr === 'live' ||
        (scorecard?.fetchLiveInningsData?.length !== 0 && scorecard?.fetchLiveInningsData[0]?.aBatters?.length !== 0)
      ) {
        return (
          <MatchDetailScorecard style={styles} data={scorecard} squad={squadData} matchDetail={matchData} matchOverView={matchOverView} />
        )
      } else {
        return <NoData />
      }
    } else if (type === 'fantasy-tips') {
      if (status === 'live' || status === 'completed') {
        return (
          <MatchDetailFantasyPlatForm data={fantasyPlatFormData?.getFantasyTipsFront} matchId={matchData?._id} overview={overViewData} />
        )
      } else return <MatchDetailFantasyTips style={styles} data={fantasyData?.getOverviewFront} />
    } else if (type === 'fixtures-and-results') return upcomingData?.length !== 0 ? <MatchDetailUpcoming data={upcomingData} /> : <NoData />
    // else if (type === 'fantasy-tips' && (status === 'live' || status === 'completed'))
    // else if (type === 'live-commentary') return (((matchDetail?.bIsCommentary && commentaryData.length !== 0) || liveScoreData?.length !== 0) || commentaryOpen.current === true) ? <MatchDetailCommentary data={commentaryData} matchDetail={matchDetail} liveScoreData={liveScoreData} handleCommentary={handleCommentary} /> : <NoData />
    else if (type === 'results') return resultData?.length !== 0 ? <MatchDetailUpcoming data={resultData} /> : <NoData />
    else if (type === 'overs') {
      // return recentOverData?.aResults?.length !== 0 ? <MatchDetailOvers data={recentOverData} matchDetail={matchData} /> : <NoData />
      return <MatchDetailOvers data={recentOverData} matchDetail={matchData} />
    } else if (type === 'news') {
      return newsData?.nTotal === 0 ? (
        <NoData title={<Trans i18nKey="common:NoNewsFound" />} />
      ) : (
        <MatchDetailNews data={newsData} seriesId={matchData?.oSeries?._id} />
      )
    } else if (type === 'live-blog' && liveArticleData?.iEventId) {
      return liveArticleData?.oLiveArticleList?.length ? (
        <>
          <TimelineDropdown timelineData={timelineData} />
          <div className="pt-1 d-flex flex-row w-100">
            <div className={`${styles.liveArticleContainer} common-box`}>
              <CommonContent>
                <LiveArticle
                  setTimelineData={setTimelineData}
                  disableMatchCard
                  liveArticleList={liveArticleData?.oLiveArticleList}
                  liveEventId={liveArticleData?.iEventId}
                />
              </CommonContent>
            </div>
            <div className={`${styles.timelineContainer} ${styles.timelineMain} common-box mb-0 p-1`}>
              <Timeline timelineData={timelineData} />
            </div>
          </div>
        </>
      ) : (
        <NoData />
      )
    } else if (type === 'squads') {
      return (
        <>
          <MatchXI
            data={playingXI}
            status={matchDetail?.sStatusStr}
            isOutSideCountryPlane={matchDetail?.oSeries?._id === '63f052b9d5e097df610db62d'}
          />
          <Ads
            id="div-ad-gpt-138639789-Desktop_AP_MID-1646637168-0"
            adIdDesktop="Crictracker_New_Desktop_AP_BTF_728x90"
            adIdMobile="Crictracker_New_Mobile_AP_MID2_300x250"
            slotId="23148549803"
            dimensionDesktop={[728, 90]}
            dimensionMobile={[300, 250]}
            mobile={true}
            className="text-center mb-3 mb-md-4"
          />
        </>
      )
    } else if (type === 'shorts') {
      return (
        <ReelMain
          isMobileWebView
          hideLogo
          className={`${styles.shorts} br-md py-md-3`}
          seoData={{ sSlug: allRoutes.matchDetailShorts(`${seo?.sSlug}/`) }}
        />
      )
    } else {
      if (
        (matchData?.sStatusStr === 'live' || matchData?.sStatusStr === 'completed' || matchData?.sStatusStr === 'cancelled') &&
        ((matchData?.bIsCommentary && commentaryData?.length !== 0) || commentaryOpen.current)
      ) {
        return (
          <CommentaryV2
            data={commentaryData}
            matchDetail={matchData}
            liveScoreData={liveScoreData}
            handleCommentary={handleCommentary}
            playingXI={playingXI}
          />
        )
        // return (
        //   <MatchDetailCommentary
        //     data={commentaryData}
        //     matchDetail={matchDetail}
        //     liveScoreData={liveScoreData}
        //     handleCommentary={handleCommentary}
        //     playingXI={playingXI}
        //   />
        // )
      } else if (matchData?.sTitle !== null && (matchOverView?.oTeam1 || matchOverView?.oTeam2)) {
        return <MatchDetailOverview data={matchOverView} matchDetail={matchData} />
      } else {
        return <NoData />
      }
    }
  }
  const isBottomNav = matchData?.sStatusStr === 'live' && (matchData?.oLiveScore?.nRunrate > 0 || matchData?.oLiveScore?.sRequiredRunrate)
  return (
    <Layout data={{ ...matchDetail, oSeo: seo }} matchDetail={{ ...matchDetail, commentary: commentaryData }}>
      <main className={`${styles.matchPage} common-section pt-1 pt-sm-3`}>
        <Container>
          <div className="mb-1 mb-sm-3 text-center" style={{ minHeight: '100px' }}>
            {/* {width > 767 && ( // Desktop top */}
            <Ads
              id="div-ad-gpt-138639789-1646635806-0"
              adIdDesktop="Crictracker_New_Desktop_LiveScore_ATF1_728x90"
              adIdMobile="Crictracker_New_Mobile_LiveScore_ATF_300x250"
              dimensionDesktop={[728, 90]}
              dimensionMobile={[300, 250]}
              slotId="23148549803"
              mobile
              className={'text-center mb-4'}
            />
            {/* )} */}
          </div>
          <Row>
            <Col lg={9} className={`left-content ${isGlanceView ? 'mb-5' : ''}`}>
              <MatchHeader data={matchData} liveScoreData={liveScoreData} currentTab={type} />
              {matchData?.sStatusStr !== 'scheduled' && <RecentOver matchDetail={matchData} />}
              <div className={`mb-3 ${isGlanceView ? '' : 'mt-3'} d-none d-md-flex`}>
                <Ads
                  id="div-ad-gpt-138639789-1660147282-1"
                  adIdDesktop="Crictracker_New_Desktop_LiveScore_MID1_728x90"
                  adIdMobile="Crictracker_New_Mobile_LiveScore_ATF_300x250"
                  dimensionDesktop={[728, 90]}
                  dimensionMobile={[300, 250]}
                  slotId="23148549803"
                  hideOn="mobile"
                  mobile
                  className={'text-center w-100'}
                />
              </div>
              {isGlanceView && (
                <GlanceAd
                  id={`div-gpt-ad-1896${new Date().getTime() * ((Math.random() + 1) * 1000)}`}
                  adId="Crictracker_small_banner_top"
                  dimension={[
                    [320, 100],
                    [320, 50],
                    [300, 50]
                  ]}
                  adUnitName="Crictracker_Sportstab_InArticleMedium_Mid1"
                  placementName="InArticleMedium"
                  className="d-flex justify-content-center"
                  width={320}
                  height={50}
                  pageName="Crictracker SportsTab"
                />
              )}
              <CommonNav
                items={matchDetailNav(
                  matchData?.oSeo?.sSlug,
                  router.asPath,
                  matchData?.oSeries?.oCategory?.oSeo?.sSlug || matchData?.oSeries?.oSeo?.sSlug,
                  matchData?.sStatusStr,
                  matchData?.oSeries?.nTotalTeams,
                  matchData?.bIsCommentary,
                  matchData.iEventId,
                  isGlanceView,
                  categoryPages
                )}
                isSticky
                isMatch
                className={`mt-1 mt-sm-3 ${styles.matchNav} ${
                  matchData?.sStatusStr === 'live' ? styles.navBottom : styles.noBottomInfoNav
                } ${isBottomNav ? '' : styles.noBottomInfoNav} `}
                // ${(matchDetail?.sStatusStr === 'live' || matchDetail?.sStatusStr === 'completed' || matchDetail?.sStatusStr === 'cancelled') ? styles.navBottom : ''}
                // ${((matchDetail?.sStatusStr === 'live' && (matchDetail?.oLiveScore?.nRunrate > 0 || matchDetail?.oLiveScore?.sRequiredRunrate)) ||
                //   (matchDetail?.sStatusStr === 'completed' && (matchDetail?.oMom?._id || matchDetail?.oMos?._id))) ? '' : styles.noBottomInfoNav}
              />
              {tab()}
            </Col>
            <Col lg={3} className="common-sidebar d-none d-lg-block">
              <CurrentSeries data={sideBarData?.currentSeries} />
              {matchData?.aPoll?.length > 0 &&
                matchData?.aPoll[0]?.aSlides?.map((p) => (
                  <MatchPoll
                    key={p?._id}
                    poll={{ ...p, dEndDate: matchData?.aPoll[0]?.dEndDate, parentId: matchData?.aPoll[0]?._id }}
                    teamOneImage={matchData?.oTeamScoreA?.oTeam?.oImg}
                    teamTwoImage={matchData?.oTeamScoreB?.oTeam?.oImg}
                  />
                ))}
              <TrendingNews defaultData={sideBarData?.trendingNews} />
              {/* <CricketPedia /> */}
              {liveArticleData?.iEventId && liveArticleData?.oLiveArticleList?.length ? (
                <div className={`${styles.timelineContainer} ${styles.timelineSidebar} common-box mb-0 p-1 w-100`}>
                  <Timeline timelineData={timelineData} />
                </div>
              ) : null}
              <Ads
                id="div-ad-gpt-138639789-1716301699-6"
                adIdDesktop="Crictracker_New_Desktop_LiveScore_RightMID_300x600"
                slotId="23148549803"
                hideOn="mobile"
                dimensionDesktop={[300, 600]}
                className="text-center sticky-ads position-sticky mb-2"
              />
            </Col>
          </Row>
        </Container>
      </main>
    </Layout>
  )
}

MatchDetail.propTypes = {
  type: PropTypes.string,
  matchDetail: PropTypes.object,
  scorecard: PropTypes.object,
  fantasyData: PropTypes.object,
  fantasyPlatFormData: PropTypes.object,
  overViewData: PropTypes.object,
  commentaryData: PropTypes.array,
  matchOverView: PropTypes.object,
  squadData: PropTypes.object,
  upcomingData: PropTypes.array,
  newsData: PropTypes.object,
  recentOverData: PropTypes.object,
  liveScoreData: PropTypes.array,
  seoData: PropTypes.object,
  roundData: PropTypes.array,
  resultData: PropTypes.array,
  playingXI: PropTypes.object,
  liveArticleData: PropTypes.object,
  sideBarData: PropTypes.object,
  catSubPages: PropTypes.array
}

export default Error(MatchDetail)

export async function getServerSideProps({ res, resolvedUrl, query }) {
  const [graphql, articleQuery, utils, routes, matchQuery, fixtureQuery, rankingsQuery, seoQuery, homeQuery] = await Promise.all([
    import('@shared-components/queryGraphql'),
    import('@graphql/article/article.query'),
    import('@shared/utils'),
    import('@shared/constants/allRoutes'),
    import('@graphql/match/match.query'),
    import('@graphql/series/fixtures.query'),
    import('@graphql/globalwidget/rankings.query'),
    import('@graphql/seo/seo.query'),
    import('@graphql/home/home.query')
  ])

  const slug = resolvedUrl
    .split('?')[0]
    .split('/')
    .filter((x) => x)
  const currentPage = slug[slug.length - 1]
  const mSlug = routes.matchDetailTabSlug.includes(currentPage) ? slug.slice(0, -1).join('/') : slug.join('/')

  try {
    res.setHeader('Cache-Control', 'public, max-age=120')

    const { data: seoData } = await graphql.default(articleQuery.GET_ARTICLE_ID, { input: { sSlug: mSlug } })

    // Check Redirection
    const { redirectStatus, eCode, returnObj, props } = utils.checkRedirectionStatus(seoData?.getSeoData, query?.amp)
    if (redirectStatus && props) {
      res.statusCode = eCode
      return { props }
    } else if (redirectStatus) return returnObj

    if (seoData?.getSeoData?.iId) {
      const { data: match } = await graphql.default(matchQuery.MATCH_DETAIL, { input: { _id: seoData?.getSeoData?.iId } })
      if (!match?.getMatchById) return { notFound: true }
      const api = [
        graphql?.default(seoQuery.GET_SUB_PAGES_BY_SLUG, {
          input: {
            sSlug: match?.getMatchById?.oSeries?.oCategory?.oSeo?.sSlug || match?.getMatchById?.oSeries?.oSeo?.sSlug,
            eSubType: 's'
          }
        }),
        graphql.default(matchQuery.GET_MATCH_PLAYING_XI, { input: { iMatchId: seoData?.getSeoData?.iId } }),
        graphql.default(rankingsQuery.CURRENT_SERIES_LIST),
        graphql.default(homeQuery.TRENDING_NEWS, { input: { nSkip: 0, nLimit: 3 } })
      ]

      if (slug.length > 2 && routes.matchDetailTabSlug.includes(currentPage)) {
        if (currentPage === 'full-scorecard') {
          api.push(
            graphql.default(matchQuery.FULL_SCOREBOARD, { input: { iMatchId: match?.getMatchById?._id } }),
            graphql.default(matchQuery.GET_MATCH_SQUAD, { input: { iMatchId: match?.getMatchById?._id } })
          )

          if (match?.getMatchById?.sStatusStr === 'completed' || match?.getMatchById?.sStatusStr === 'live') {
            api.push(graphql.default(matchQuery.GET_MATCH_OVERVIEW, { input: { iMatchId: match?.getMatchById?._id } }))
            const [subPages, playingXI, currentSeries, trendingNews, scoreBoard, squadData, overview] = await Promise.allSettled(api)
            return {
              props: {
                type: currentPage,
                catSubPages: subPages?.value?.data?.getSeosBySlug || [],
                matchDetail: match?.getMatchById,
                matchOverView: overview?.value?.data?.getMatchOverviewFront || null,
                scorecard: scoreBoard?.value?.data || null,
                squadData: squadData?.value?.data || null,
                seoData: seoData?.getSeoData,
                playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
                sideBarData: {
                  currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                  trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
                }
              }
            }
          }
          const [subPages, playingXI, currentSeries, trendingNews, scoreBoard, squadData] = await Promise.allSettled(api)
          return {
            props: {
              type: currentPage,
              catSubPages: subPages?.value?.data?.getSeosBySlug || [],
              matchDetail: match?.getMatchById,
              scorecard: scoreBoard?.value?.data || null,
              squadData: squadData?.value?.data || null,
              seoData: seoData?.getSeoData,
              playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
              sideBarData: {
                currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
              }
            }
          }
        } else if (currentPage === 'fantasy-tips') {
          if (match?.getMatchById?.sStatusStr === 'live' || match?.getMatchById?.sStatusStr === 'completed') {
            const { data } = await graphql.default(matchQuery.FANTASY_TIPS_DETAILS, { input: { iMatchId: match?.getMatchById?._id } })
            // console.log(data?.getOverviewFront)
            if (data?.getOverviewFront?.aCricPrediction?.length) {
              api.push(
                graphql.default(matchQuery.GET_FANTASY_PLATFORM, {
                  input: {
                    iMatchId: match?.getMatchById?._id,
                    ePlatformType: data?.getOverviewFront?.aCricPrediction[0]?.ePlatformType
                    // ePlatformType: 'de'
                    // -------NOTE----------
                    // Temporary remove platform dropdown will add in future as per client requirements
                    // data?.getOverviewFront?.aCricPrediction?.length === 0 ? 'de' : data?.getOverviewFront?.aCricPrediction[0]?.ePlatformType
                  }
                })
              )
            }
            const [subPages, playingXI, currentSeries, trendingNews, fantasyPlatform] = await Promise.allSettled(api)
            return {
              props: {
                type: currentPage,
                catSubPages: subPages?.value?.data?.getSeosBySlug || [],
                matchDetail: match?.getMatchById,
                fantasyPlatFormData: fantasyPlatform?.value?.data || null,
                overViewData: data?.getOverviewFront || null,
                seoData: seoData?.getSeoData,
                playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
                sideBarData: {
                  currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                  trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
                }
              }
            }
          } else {
            api.push(graphql.default(matchQuery.FANTASY_TIPS_DETAILS, { input: { iMatchId: match?.getMatchById?._id } }))
            const [subPages, playingXI, currentSeries, trendingNews, fantasyTips] = await Promise.allSettled(api)

            return {
              props: {
                type: currentPage,
                catSubPages: subPages?.value?.data?.getSeosBySlug || [],
                matchDetail: match?.getMatchById,
                fantasyData: fantasyTips?.value?.data || null,
                seoData: seoData?.getSeoData,
                playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
                sideBarData: {
                  currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                  trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
                }
              }
            }
          }
        } else if (currentPage === 'fixtures-and-results') {
          api.push(
            graphql.default(fixtureQuery.FIXTURES_LIST, {
              input: { iSeriesId: match?.getMatchById?.oSeries?._id, nOrder: 1, sSortBy: 'dStartDate', sStatusStr: 'scheduled' }
            })
          )
          const [subPages, playingXI, currentSeries, trendingNews, fixture] = await Promise.allSettled(api)
          return {
            props: {
              type: currentPage,
              catSubPages: subPages?.value?.data?.getSeosBySlug || [],
              matchDetail: match?.getMatchById,
              upcomingData: fixture?.value?.data?.fetchFixuresData || null,
              seoData: seoData?.getSeoData,
              playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
              sideBarData: {
                currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
              }
            }
          }
        } else if (currentPage === 'results') {
          api.push(
            graphql.default(fixtureQuery.FIXTURES_LIST, {
              input: { iSeriesId: match?.getMatchById?.oSeries?._id, nOrder: 1, sSortBy: 'dStartDate', sStatusStr: 'completed' }
            })
          )
          const [subPages, playingXI, currentSeries, trendingNews, fixture] = await Promise.allSettled(api)
          return {
            props: {
              type: currentPage,
              catSubPages: subPages?.value?.data?.getSeosBySlug || [],
              matchDetail: match?.getMatchById,
              resultData: fixture?.value?.data?.fetchFixuresData || null,
              seoData: seoData?.getSeoData,
              playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
              sideBarData: {
                currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
              }
            }
          }
        } else if (currentPage === 'news') {
          const { GET_SERIES_NEWS_VIDEOS } = await import('@graphql/series/home.query')
          api.push(
            graphql.default(GET_SERIES_NEWS_VIDEOS, {
              input: { iSeriesId: match?.getMatchById?.oSeries?._id, eType: 'n', nLimit: 7, nSkip: 1 }
            })
          )
          const [subPages, playingXI, currentSeries, trendingNews, news] = await Promise.allSettled(api)
          return {
            props: {
              type: currentPage,
              catSubPages: subPages?.value?.data?.getSeosBySlug || [],
              matchDetail: match?.getMatchById,
              newsData: news?.value?.data?.listSeriesArticlesVideosFront?.oArticles || null,
              seoData: seoData?.getSeoData,
              playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
              sideBarData: {
                currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
              }
            }
          }
        } else if (currentPage === 'overs') {
          api.push(
            graphql.default(matchQuery.GET_MATCH_OVERS, {
              input: { iMatchId: match?.getMatchById?._id, nLimit: 5, nSkip: 0, nInningNumber: match?.getMatchById?.nLatestInningNumber }
            })
          )
          const [subPages, playingXI, currentSeries, trendingNews, overs] = await Promise.allSettled(api)
          return {
            props: {
              type: currentPage,
              catSubPages: subPages?.value?.data?.getSeosBySlug || [],
              matchDetail: match?.getMatchById,
              recentOverData: overs?.value?.data?.listMatchOvers || null,
              seoData: seoData?.getSeoData,
              playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
              sideBarData: {
                currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
              }
            }
          }
        } else if (currentPage === 'live-blog') {
          const liveArticleData = {}
          if (match?.getMatchById?.iEventId) {
            const { getLiveArticleData } = await import('@shared/libs/live-article')
            api.push(getLiveArticleData({ iEventId: match?.getMatchById?.iEventId }))
          }

          const [subPages, playingXI, currentSeries, trendingNews, liveBlog] = await Promise.allSettled(api)
          liveArticleData.iEventId = match?.getMatchById?.iEventId
          liveArticleData.oLiveArticleList = liveBlog?.value?.oLiveArticleList || null
          liveArticleData.oLiveArticleEvent = liveBlog?.value?.oLiveArticleEvent || null

          return {
            props: {
              type: currentPage,
              catSubPages: subPages?.value?.data?.getSeosBySlug || [],
              matchDetail: match?.getMatchById,
              liveArticleData: liveArticleData,
              seoData: seoData?.getSeoData,
              playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
              sideBarData: {
                currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
              }
            }
          }
        } else if (currentPage === 'squads' || currentPage === 'shorts') {
          const [subPages, playingXI, currentSeries, trendingNews] = await Promise.allSettled(api)
          return {
            props: {
              type: currentPage,
              catSubPages: subPages?.value?.data?.getSeosBySlug || [],
              matchDetail: match?.getMatchById,
              seoData: seoData?.getSeoData,
              playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
              sideBarData: {
                currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
              }
            }
          }
        }
        return { notFound: true }
        // return { props: { error: JSON.stringify(seoData) } }
      } else {
        // console.log(match?.getMatchById?.sStatusStr)
        if (
          (match?.getMatchById?.sStatusStr === 'completed' ||
            match?.getMatchById?.sStatusStr === 'live' ||
            match?.getMatchById?.sStatusStr === 'cancelled') &&
          match?.getMatchById?.bIsCommentary
        ) {
          if (match?.getMatchById?.sStatusStr === 'live') {
            res.setHeader('Cache-Control', 'no-store, no-cache, must-revalidate, proxy-revalidate')
            res.setHeader('Pragma', 'no-cache')
            res.setHeader('Expires', '0')
          }

          api.push(
            graphql.default(matchQuery.GET_COMMENTARY_V2, {
              input: {
                iMatchId: match?.getMatchById?._id,
                nLimit: 15,
                eHighLight: query?.eHighLight || null,
                nInningNumber: query?.nInningNumber ? Number(query?.nInningNumber) : null
              }
            }),
            graphql.default(matchQuery.LIVE_INNING_DATA, {
              input: { iMatchId: match?.getMatchById?._id, nInningNumber: match?.getMatchById?.nLatestInningNumber }
            })
          )
          const [subPages, playingXI, currentSeries, trendingNews, commentary, liveScoreData] = await Promise.allSettled(api)
          return {
            props: {
              type: slug[slug.length - 1],
              catSubPages: subPages?.value?.data?.getSeosBySlug || [],
              // commentaryData: commentary?.value?.data?.listMatchCommentaries || null,
              commentaryData: commentary?.value?.data?.listMatchCommentariesV2 || null,
              matchDetail: match?.getMatchById,
              liveScoreData: liveScoreData?.value?.data?.fetchLiveInningsData || null,
              seoData: seoData?.getSeoData,
              playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
              sideBarData: {
                currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
              }
            }
          }
        } else {
          api.push(graphql.default(matchQuery.GET_MATCH_OVERVIEW, { input: { iMatchId: match?.getMatchById?._id } }))

          const [subPages, playingXI, currentSeries, trendingNews, overview] = await Promise.allSettled(api)
          return {
            props: {
              catSubPages: subPages?.value?.data?.getSeosBySlug || [],
              matchDetail: match?.getMatchById,
              matchOverView: overview?.value?.data?.getMatchOverviewFront || null,
              seoData: seoData?.getSeoData,
              playingXI: playingXI?.value?.data?.getMatchOverviewFront || null,
              sideBarData: {
                currentSeries: currentSeries?.value?.data?.getCurrentPopularSeries || null,
                trendingNews: trendingNews?.value?.data?.getTrendingNews?.aResults || null
              }
            }
          }
        }
      }
    } else {
      return { notFound: true }
    }
  } catch (e) {
    // console.dir({ matchDetail: e }, { depth: null })
    res.setHeader('Cache-Control', 'no-cache')
    const status = utils.handleApiError(e, resolvedUrl)
    return status
  }
}
